import { AccessorialType, PaymentTerms } from '@numeo/types-core'
import { invoicingStatusOf, type CreateInvoiceParams, type InvoiceChargeParams, type TmsInvoiceRow, type TmsReadinessReason } from '@numeo/types-main-web-app'

/** The fewest characters a reason for confirming a document match takes (v17). */
const DOCUMENT_REASON_MIN = 8

/** The fewest characters a reason for skipping a POD exception takes (v17). */
const SKIP_REASON_MIN = 5

/** The reason codes a POD that is on file but does not pass the check carries: the load needs a person to confirm it. */
const POD_ISSUE_CODES: ReadonlySet<string> = new Set(['reference_mismatch', 'route_mismatch', 'unsigned', 'pod_not_accepted'])

/** One additional charge as the form holds it: amounts stay as typed until they are sent. */
export interface ChargeDraft {
    id: string
    type: string
    amount: string
    description: string
}

/** The payment terms the drawer offers, with the words v17 shows. */
export const PAYMENT_TERMS_OPTIONS: ReadonlyArray<{ value: PaymentTerms; label: string }> = [
    { value: PaymentTerms.NET_15, label: 'Net 15' },
    { value: PaymentTerms.NET_30, label: 'Net 30' },
    { value: PaymentTerms.NET_45, label: 'Net 45' },
    { value: PaymentTerms.NET_60, label: 'Net 60' },
]

/** The terms a new draft takes when the row carries none. */
export const DEFAULT_PAYMENT_TERMS = PaymentTerms.NET_30

/** "DETENTION" → "Detention", "FUEL_SURCHARGE" → "Fuel surcharge": the words of an accessorial type in the charge select. */
export const chargeTypeLabel = (type: string): string => {
    const words = type.replace(/_/g, ' ').toLowerCase()
    return words.charAt(0).toUpperCase() + words.slice(1)
}

/** The accessorial types a charge may carry, in the card's own order. */
export const CHARGE_TYPE_OPTIONS = Object.values(AccessorialType).map((type) => ({ value: type as string, label: chargeTypeLabel(type) }))

/** The amount a typed charge stands for: blank or unparsable is zero, the card then ignores the line. */
export const chargeAmountOf = (amount: string): number => {
    const value = Number(amount.replace(/[$,\s]/g, ''))
    return amount.trim() === '' || !Number.isFinite(value) ? 0 : value
}

/** The first problem with the additional charges, in the words the drawer shows, or null when they may be sent. */
export function chargeErrors(charges: ChargeDraft[]): string | null {
    for (const charge of charges) {
        const raw = charge.amount.replace(/[$,\s]/g, '')
        if (raw !== '' && (!Number.isFinite(Number(raw)) || Number(raw) < 0)) return 'Additional charges must be zero or a positive amount.'
        if (chargeAmountOf(charge.amount) > 0 && !charge.description.trim()) return 'Provide a description and supporting reference for additional charges.'
        if (chargeAmountOf(charge.amount) > 0 && !charge.type) return 'Choose a type for each additional charge.'
    }
    return null
}

/** The charges the card is sent: each with an amount above zero, on the load the drawer is for. */
const chargesParamsOf = (loadNumber: string, charges: ChargeDraft[]): InvoiceChargeParams[] =>
    charges
        .filter((charge) => chargeAmountOf(charge.amount) > 0)
        .map((charge) => ({ load_number: loadNumber, type: charge.type, amount_usd: chargeAmountOf(charge.amount), description: charge.description.trim() }))

/** The invoice total: the line haul plus every charge above zero. */
export const invoiceTotalOf = (linehaul: number, charges: ChargeDraft[]): number =>
    charges.reduce((total, charge) => total + Math.max(0, chargeAmountOf(charge.amount)), linehaul)

/** The reason for confirming a document match, or the v17 message when it is too short. */
export const confirmReasonError = (reason: string): string | null =>
    reason.trim().length < DOCUMENT_REASON_MIN ? 'Enter a reason for confirming this document match.' : null

/** The reason for skipping a POD exception, or the message when it is too short. */
export const skipReasonError = (reason: string): string | null => (reason.trim().length < SKIP_REASON_MIN ? 'Enter a reason for skipping the POD issue.' : null)

/** A `YYYY-MM-DD` date for a calendar day, in the viewer's zone. */
export const todayIsoDate = (now: Date = new Date()): string => {
    const pad = (value: number) => String(value).padStart(2, '0')
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/** Whether the load has no rate yet: the drawer then shows an editable linehaul field, and the card's reason says so. */
export const ratePendingOf = (row: Pick<TmsInvoiceRow, 'reasons'>): boolean => (row.reasons ?? []).some((reason) => reason.code === 'rate_required')

/** The plain words of one finding of the POD check, the same finding the table row names ("POD route mismatch"). */
const findingSentenceOf = (reason: TmsReadinessReason): string | null => {
    switch (reason.code) {
        case 'reference_mismatch':
            if (reason.pod_reference && reason.load_reference) return `The POD references #${reason.pod_reference}, but this load is #${reason.load_reference}.`
            if (reason.pod_reference) return `The POD references #${reason.pod_reference}, which is not this load's reference.`
            return "The reference on the POD does not match this load's reference."
        case 'route_mismatch':
            return "The POD's pickup or delivery address does not match this load's route."
        case 'unsigned':
            return 'The POD carries no consignee signature.'
        case 'poor_quality':
            return `The POD is hard to read${reason.quality_reason ? ` (${reason.quality_reason})` : ''}.`
        case 'missing_pages': {
            const pages = reason.missing_page_numbers ?? []
            return pages.length ? `The POD is missing page${pages.length === 1 ? '' : 's'} ${pages.join(', ')}.` : 'The POD is missing pages.'
        }
        default:
            return null
    }
}

/**
 * What the yellow note of a POD that does not match this delivery says: each finding of the check the row shows (its reasons, then its
 * warnings), in plain words. With no finding, the board's own sentence for the POD issue (e.g. "on file but not accepted"), else null.
 */
export const podIssueSentenceOf = (row: Pick<TmsInvoiceRow, 'reasons' | 'warnings'>): string | null => {
    const seen = new Set<string>()
    const findings: string[] = []
    for (const reason of [...(row.reasons ?? []), ...(row.warnings ?? [])]) {
        if (seen.has(reason.code)) continue
        seen.add(reason.code)
        const sentence = findingSentenceOf(reason)
        if (sentence) findings.push(sentence)
    }
    if (findings.length) return findings.join(' ')
    return (row.reasons ?? []).find((reason: TmsReadinessReason) => POD_ISSUE_CODES.has(reason.code))?.message ?? null
}

/** Whether the load has no rate confirmation: it cannot be invoiced (the create card takes Ready loads only), so it is never skipped. */
export const rateConMissingOf = (row: Pick<TmsInvoiceRow, 'reasons' | 'documents'>): boolean =>
    row.documents?.rate_con === false || (row.reasons ?? []).some((reason) => reason.code === 'missing_ratecon')

/**
 * Whether Approve & create draft is enabled: the load is ready, or its only gap is a rate the drawer is about to save (a valid one
 * typed). A POD issue is never approved here: it is confirmed or skipped first.
 */
export const canApprove = (row: TmsInvoiceRow, rateTyped: number | null): boolean => {
    const status = invoicingStatusOf(row)
    if (status === 'ready') return true
    return status === 'other' && ratePendingOf(row) && rateTyped !== null
}

/** The fields of the create call the drawer sends (the rest only when given). */
interface CreateDraftInput {
    loadNumber: string
    /** Sent only when the user changed the date: an absent date is today on the server's own clock. */
    invoiceDate?: string
    paymentTerms?: PaymentTerms
    invoiceNumber?: string
    charges: ChargeDraft[]
}

/** The parameters of `tms_invoices.create` for one load: one invoice, with the number only when the user typed one. */
export const createParamsOf = ({ loadNumber, invoiceDate, paymentTerms, invoiceNumber, charges }: CreateDraftInput): CreateInvoiceParams => {
    const typed = invoiceNumber?.trim()
    const sent = chargesParamsOf(loadNumber, charges)
    return {
        load_numbers: [loadNumber],
        ...(invoiceDate ? { invoice_date: invoiceDate } : {}),
        ...(paymentTerms ? { payment_terms: paymentTerms } : {}),
        ...(typed ? { invoice_number: typed } : {}),
        ...(sent.length ? { charges: sent } : {}),
    }
}
